import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import LeadDetailSheet from "@/components/LeadDetailSheet";
import { CalendarDays, Send, TriangleAlert, CheckCircle2, Flame } from "lucide-react";

// Klik satu baris (Follow-up Telat / Lead Hot / Closing Hari Ini) langsung buka detail lead
// itu — sebelumnya cuma teks statis, harus dicari manual lagi di Pipeline/Leads.
function ClientRow({ nama, sales, right, rightClass, leadId, onSelect }) {
  const clickable = !!leadId;
  return (
    <div className={`flex justify-between text-xs rounded px-1 -mx-1 py-0.5 ${clickable ? "cursor-pointer hover:bg-white/5" : ""}`}
      onClick={clickable ? () => onSelect(leadId) : undefined}>
      <span className="truncate">{nama} <span className="text-zinc-600">· {sales}</span></span>
      <span className={`${rightClass} shrink-0 whitespace-nowrap tabular-nums`}>{right}</span>
    </div>
  );
}

export default function DailySummaryCard() {
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["daily-summary"], queryFn: async () => (await api.get("/reports/daily-summary")).data });
  if (isLoading || !data) return null;
  const s = data.stats;
  const cards = [
    ["Lead Baru", s.new_today, ""],
    ["Closing", s.closing_today, rupiah(s.omset_today)],
    ["Omset Bulan Ini", rupiah(s.omset_mtd), ""],
    ["Lead Hot", s.hot ?? 0, ""],
  ];
  return (
    <div className="card-surface rounded-2xl p-5 mb-4" data-testid="daily-summary-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><CalendarDays size={18} className="text-blue-400" />Ringkasan Harian</h3>
          <p className="text-xs text-zinc-500">Terkirim otomatis ke email admin & owner tiap pagi jam 8 WIB.</p>
        </div>
        <a href={data.wa_link} target="_blank" rel="noreferrer" data-testid="send-wa-summary">
          <Button className="h-9 bg-emerald-600 hover:bg-emerald-700"><Send size={14} className="mr-1.5" />Kirim via WhatsApp</Button>
        </a>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
        {cards.map(([l, v, sub], i) => (
          <div key={i} className="p-2.5 rounded-xl bg-[#18181B]" data-testid={`summary-stat-${i}`}>
            <div className="text-sm font-bold whitespace-nowrap tabular-nums">{v}</div>
            <div className="text-[10px] text-zinc-500">{l}</div>
            {sub && <div className="text-[10px] text-emerald-400 whitespace-nowrap tabular-nums">{sub}</div>}
          </div>
        ))}
      </div>
      {data.overdue.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-amber-400 flex items-center gap-1 mb-1.5"><TriangleAlert size={12} />Follow-up Telat</div>
          <div className="space-y-1">
            {data.overdue.slice(0, 5).map((o, i) => (
              <ClientRow key={i} nama={o.nama} sales={o.sales} right={`${o.days} hari`} rightClass="text-amber-400" leadId={o.lead_id} onSelect={setSelected} />
            ))}
          </div>
        </div>
      )}
      {(data.hot_leads || []).length > 0 && (
        <div className="mt-3" data-testid="hot-leads-list">
          <div className="text-[11px] uppercase tracking-wide text-blue-400 flex items-center gap-1 mb-1.5"><Flame size={12} />Lead Hot — Prioritaskan</div>
          <div className="space-y-1">
            {data.hot_leads.slice(0, 5).map((o, i) => (
              <ClientRow key={i} nama={o.nama} sales={o.sales} right={`Skor ${o.score}`} rightClass="text-blue-300" leadId={o.lead_id} onSelect={setSelected} />
            ))}
          </div>
        </div>
      )}
      {data.closing_today.length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-emerald-400 flex items-center gap-1 mb-1.5"><CheckCircle2 size={12} />Closing Hari Ini</div>
          <div className="space-y-1">
            {data.closing_today.slice(0, 5).map((o, i) => (
              <ClientRow key={i} nama={o.nama} sales={o.sales} right={rupiah(o.nilai)} rightClass="text-emerald-400" leadId={o.lead_id} onSelect={setSelected} />
            ))}
          </div>
        </div>
      )}
      <LeadDetailSheet leadId={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
